// Pure helpers for the OTA approval UI. User-visible text comes from the `edges.ota.*` locale keys.
import i18n from "../../i18n";
import { formatDate } from "../../utils/i18nFormat";

const DAY = 86400000;
const pad = (n) => String(n).padStart(2, "0");

// Date -> value for <input type="datetime-local"> (local time)
export const toLocalInput = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Next 02:00 -> 04:00 local.
export function defaultWindow(now = new Date()) {
  const s = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 2, 0);
  if (s <= now) s.setDate(s.getDate() + 1);
  const e = new Date(s.getFullYear(), s.getMonth(), s.getDate(), 4, 0);
  return { start: toLocalInput(s), end: toLocalInput(e) };
}

// Returns a localized error string or null when the window is acceptable.
export function validateWindow(start, end, now = new Date()) {
  const s = new Date(start);
  const e = new Date(end);
  if (!start || !end || isNaN(s) || isNaN(e)) return i18n.t("edges.ota.window.required");
  if (e <= s) return i18n.t("edges.ota.window.endAfterStart");
  if (s < now) return i18n.t("edges.ota.window.startInPast");
  if (e - s > 14 * DAY) return i18n.t("edges.ota.window.tooLong");
  return null;
}

export const toUtcIso = (local) => new Date(local).toISOString();

// results: [{edgeId, release: {id,...}|null}] -> {groups:[{release, edgeIds}], none:[edgeId]}
// Groups sorted by size desc so the biggest is the default pick.
export function groupByRelease(results) {
  const map = new Map();
  const none = [];
  results.forEach(({ edgeId, release }) => {
    if (!release) return none.push(edgeId);
    if (!map.has(release.id)) map.set(release.id, { release, edgeIds: [] });
    map.get(release.id).edgeIds.push(edgeId);
  });
  return { groups: [...map.values()].sort((a, b) => b.edgeIds.length - a.edgeIds.length), none };
}

// Backend reason strings -> locale keys; an unknown reason is shown as the backend sent it.
const REASONS = {
  "active VPN session": "edges.ota.reason.activeVpn",
  "release not offered to this edge": "edges.ota.reason.notOffered",
  "not found": "edges.ota.reason.notFound",
  "inactive edge": "edges.ota.reason.inactive",
};
export const reasonTr = (r) => (REASONS[r] ? i18n.t(REASONS[r]) : r || i18n.t("edges.ota.reason.skipped"));

export const releaseKind = (r) => i18n.t(r?.artifact_type === "hotfix" ? "edges.ota.kind.hotfix" : "edges.ota.kind.os");

export const truncate = (s, n = 300) => (s && s.length > n ? `${s.slice(0, n)}…` : s || "");

// Run fn over items with at most `limit` in flight; results keep input order.
export async function runPool(items, limit, fn) {
  const out = new Array(items.length);
  let i = 0;
  const worker = async () => {
    while (i < items.length) {
      const k = i++;
      out[k] = await fn(items[k]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

export const errDetail = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join("; ") : fallback;
};

// Reason text for a failed GET /edges/{id}/ota lookup.
export const lookupErrorReason = (err) => {
  const detail = errDetail(err, null);
  const s = err?.response?.status;
  return i18n.t("edges.ota.reason.lookupFailed", { detail: detail || (s ? `HTTP ${s}` : err?.message || i18n.t("edges.ota.reason.unknownError")) });
};

// entries: [{edgeId, release, error?}] -> {approve:[edgeId], skipped:[{edgeId, reason}]}
// Everything not approved is listed with a reason; nothing disappears silently.
export function splitSelection(entries, releaseId) {
  const approve = [];
  const skipped = [];
  entries.forEach(({ edgeId, release, error }) => {
    if (error) skipped.push({ edgeId, reason: error });
    else if (!release) skipped.push({ edgeId, reason: i18n.t("edges.ota.reason.noUpdate") });
    else if (release.id === releaseId) approve.push(edgeId);
    else skipped.push({ edgeId, reason: i18n.t("edges.ota.reason.otherRelease", { version: release.version }) });
  });
  return { approve, skipped };
}

// Device certificate pill. Returns null when the value is not provided (non-superadmin).
// -> {text, sub, kind: "ok"|"warn"|"off"|"none"}
export function certInfo(edge) {
  const d = edge?.cert_days_remaining;
  const at = edge?.cert_not_after;
  if (typeof d !== "number") return { text: "—", sub: "", kind: "none" };
  const sub = at && !isNaN(new Date(at)) ? formatDate(at, { dateStyle: "medium" }) : "";
  if (d < 0) return { text: i18n.t("edges.cert.expiredAgo", { n: -d }), sub, kind: "off" };
  return { text: i18n.t("edges.cert.days", { n: d }), sub, kind: d < 30 ? "warn" : "ok" };
}

export const certExpiringSoon = (edge) => typeof edge?.cert_days_remaining === "number" && edge.cert_days_remaining < 30;
